/**
 * Assigned post-it tracking drawer (occurrences + users).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { TrackingOccurrenceList } from './TrackingOccurrenceList';
import { TrackingUserList } from './TrackingUserList';
import {
  getTrackingOccurrences,
  getTrackingUsers,
  forceCompleteForUser,
} from '@/services/post-its.service';
import type { TrackingOccurrence, PostItStatus } from '@/types/post-it.types';
import {
  getEffectivePeriodKey,
  pickCurrentTrackingOccurrence,
} from '@/components/post-its/post-it-table-utils';

/** Props for {@link TrackingDrawer}. */
interface TrackingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateTitle: string;
}

/** Assigned post-it tracking drawer (occurrences + users). */
export function TrackingDrawer({
  isOpen,
  onClose,
  templateId,
  templateTitle,
}: Readonly<TrackingDrawerProps>) {
  const queryClient = useQueryClient();
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PostItStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const pageSize = 20;

  const { data: occurrences, isLoading: loadingOccurrences } = useQuery({
    queryKey: ['tracking-occurrences', templateId],
    queryFn: () => getTrackingOccurrences(templateId),
    enabled: isOpen,
  });

  const currentOccurrence = pickCurrentTrackingOccurrence(occurrences);
  const effectivePeriodKey = getEffectivePeriodKey(selectedPeriodKey, currentOccurrence);

  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: [
      'tracking-users',
      templateId,
      effectivePeriodKey,
      statusFilter,
      searchQuery,
      currentPage,
    ],
    queryFn: () =>
      getTrackingUsers(templateId, {
        periodKey: effectivePeriodKey!,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchQuery || undefined,
        page: currentPage,
        pageSize,
      }),
    enabled: isOpen && effectivePeriodKey !== null,
  });

  const forceCompleteMutation = useMutation({
    mutationFn: ({ userId, periodKey }: { userId: string; periodKey: string }) =>
      forceCompleteForUser(templateId, userId, periodKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tracking-occurrences', templateId] });
      void queryClient.invalidateQueries({ queryKey: ['tracking-users', templateId] });
    },
  });

  /** Switch period and reset the user list to page 1. */
  const handleOccurrenceSelect = (occurrence: TrackingOccurrence) => {
    setSelectedPeriodKey(occurrence.periodKey);
    setCurrentPage(1);
  };

  /** Coordinator override: mark one assignee done for the selected period. */
  const handleForceComplete = async (userId: string) => {
    if (!effectivePeriodKey) return;
    setLoadingUserId(userId);
    try {
      await forceCompleteMutation.mutateAsync({ userId, periodKey: effectivePeriodKey });
    } finally {
      setLoadingUserId(null);
    }
  };

  /** Search assignees and reset to page 1. */
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  /** Filter assignees by status and reset to page 1. */
  const handleStatusFilter = (status: PostItStatus | 'all') => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      size="2xl"
      header={{
        title: 'Suivi des assignations',
        subtitle: templateTitle,
      }}
      compactPadding
      className="[&_[role=dialog]]:max-h-[85vh]"
    >
      <div className="flex h-[calc(85vh-180px)] overflow-hidden">
        <div className="w-64 border-r flex flex-col bg-muted/10">
          <TrackingOccurrenceList
            occurrences={occurrences ?? []}
            isLoading={loadingOccurrences}
            selectedPeriodKey={effectivePeriodKey}
            onSelect={handleOccurrenceSelect}
          />
        </div>

        <div className="flex-1 flex flex-col">
          <TrackingUserList
            users={usersData?.users ?? []}
            total={usersData?.total ?? 0}
            page={currentPage}
            pageSize={pageSize}
            isLoading={loadingUsers}
            statusFilter={statusFilter}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            onStatusFilter={handleStatusFilter}
            onPageChange={setCurrentPage}
            onForceComplete={handleForceComplete}
            loadingUserId={loadingUserId}
            selectedOccurrence={
              occurrences?.find((o) => o.periodKey === effectivePeriodKey) ?? null
            }
          />
        </div>
      </div>
    </BaseModal>
  );
}
